import {useState, useCallback} from 'react'
import {Box, Button, IconButton, ActionList, ActionMenu, Text, Spinner} from '@primer/react'

import {KebabHorizontalIcon} from '@primer/octicons-react'
import {LABELS, VALUES} from '../constants'
import {useLocalStorage} from '@github-ui/use-safe-storage'
import {TimelineDivider} from './row/TimelineDivider'

type TimelineLoadMoreProps = {
  loadMoreTopFn?: (
    count: number,
    options?: {
      onComplete?: () => void
    },
  ) => void
  loadMoreBottomFn?: (
    count: number,
    options?: {
      onComplete?: () => void
    },
  ) => void
  numberOfTimelineItems: number
  type: 'front' | 'highlighted-before' | 'highlighted-after'
}

export function TimelineLoadMore({
  loadMoreTopFn,
  loadMoreBottomFn,
  numberOfTimelineItems,
  type,
}: TimelineLoadMoreProps) {
  const [isLoading, setIsLoading] = useState(false)
  const [isHovering, setIsHovering] = useState<'top' | 'bottom' | undefined>(undefined)
  const [isLoadingFromTop, setIsLoadingFromTop] = useLocalStorage('loadMorePrefence', loadMoreTopFn !== undefined)

  const loadMore = useCallback(
    (loadAll = false, isLoadingFromTopVar = true) => {
      const loadCount = loadAll ? numberOfTimelineItems : VALUES.timeline.pageSize
      setIsLoading(true)
      setIsHovering(undefined)
      setIsLoadingFromTop(isLoadingFromTopVar)

      if (isLoadingFromTopVar) {
        loadMoreTopFn &&
          loadMoreTopFn(loadCount, {
            onComplete: () => {
              setIsLoading(false)
            },
          })
      } else {
        loadMoreBottomFn &&
          loadMoreBottomFn(loadCount, {
            onComplete: () => {
              setIsLoading(false)
            },
          })
      }
    },
    [loadMoreBottomFn, loadMoreTopFn, numberOfTimelineItems, setIsLoadingFromTop],
  )

  const loadBeforeCallback = useCallback(() => {
    loadMore(false, true)
  }, [loadMore])

  const loadAfterCallback = useCallback(() => {
    loadMore(false, false)
  }, [loadMore])

  const loadAllMode = numberOfTimelineItems < VALUES.timeline.maxPreloadCount

  if (loadAllMode && isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 4,
        }}
      >
        <Spinner size={'small'} />
      </Box>
    )
  }

  return (
    <Box sx={{py: 2, gap: 2, display: 'flex', flexDirection: 'column'}}>
      <TimelineDivider isLoading={isLoading && isLoadingFromTop} large isHovered={isHovering === 'top'} />
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexDirection: 'row',
          border: '1px solid',
          borderColor: 'border.muted',
          borderRadius: 2,
          backgroundColor: 'canvas.default',
          boxShadow: 'shadow.small',
          overflowX: 'auto',
          scrollMarginTop: '100px',
          flexGrow: 1,
          p: 2,
        }}
        data-testid={`issue-timeline-load-more-container-${type}`}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: loadAllMode ? 'center' : 'space-between',
            alignItems: 'center',
            flexDirection: 'row',
            flexGrow: 1,
          }}
        >
          {!loadAllMode && (
            <Box sx={{width: '150px', display: 'flex', justifyContent: 'flex-start', pl: 2}}>
              <Text sx={{fontSize: 0, color: 'fg.muted'}} data-testid={`issue-timeline-load-more-count-${type}`}>
                {`${numberOfTimelineItems}`}
              </Text>
              <Text sx={{fontSize: 0, color: 'fg.muted', ml: 1}}> more items</Text>
            </Box>
          )}
          <Box sx={{display: 'flex', alignItems: 'center', gap: 2, cursor: 'pointer'}}>
            <Button
              variant="invisible"
              onClick={() => loadMore(loadAllMode, isLoadingFromTop)}
              disabled={isLoading}
              data-testid={`issue-timeline-load-more-${type}`}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                {loadAllMode ? LABELS.timeline.loadAll : LABELS.timeline.loadMore}
              </Box>
            </Button>
          </Box>
          {!loadAllMode && (
            <Box sx={{width: '150px', display: 'flex', justifyContent: 'flex-end'}}>
              <ActionMenu>
                <ActionMenu.Anchor>
                  <IconButton
                    size="small"
                    icon={KebabHorizontalIcon}
                    data-testid={`issue-timeline-load-more-options-${type}`}
                    variant="invisible"
                    aria-label="Load more actions"
                    sx={{color: 'fg.muted'}}
                  />
                </ActionMenu.Anchor>
                <ActionMenu.Overlay>
                  <ActionList sx={{width: '250px'}}>
                    {loadMoreTopFn && (
                      <ActionList.Item
                        disabled={isLoading}
                        onSelect={() => loadBeforeCallback()}
                        onMouseEnter={() => setIsHovering('top')}
                        onMouseLeave={() => setIsHovering(undefined)}
                      >
                        <Text>{LABELS.timeline.loadOlder}</Text>
                      </ActionList.Item>
                    )}
                    {loadMoreBottomFn && (
                      <ActionList.Item
                        disabled={isLoading}
                        onSelect={() => loadAfterCallback()}
                        onMouseEnter={() => setIsHovering('bottom')}
                        onMouseLeave={() => setIsHovering(undefined)}
                      >
                        <Text>{LABELS.timeline.loadNewer}</Text>
                      </ActionList.Item>
                    )}
                  </ActionList>
                </ActionMenu.Overlay>
              </ActionMenu>
            </Box>
          )}
        </Box>
      </Box>
      {!loadAllMode && (
        <TimelineDivider isLoading={isLoading && !isLoadingFromTop} large isHovered={isHovering === 'bottom'} />
      )}
    </Box>
  )
}

try{ TimelineLoadMore.displayName ||= 'TimelineLoadMore' } catch {}